import { useEffect, useState } from 'react';
import { API_BASE } from '../apiSession';

// The one admin account (the founder's). Still used for admin-only detail
// outside the admin panel, such as the model and token receipt under a chat
// reply (founder, 2026-09-27: "only you, as admin").
export const ADMIN_UID = 'MnwcS7G6ZGSsTQmkofjIl9Vu3jA3';
export const isAdminUser = (user) => Boolean(user && user.uid === ADMIN_UID);

// The server's answer per person, kept for this visit to the site, so moving
// between /admin, /admin/sync-history and /admin/database does not wait for
// GET /api/admin/me (a sign-in check and two database statements) each time.
// A change to VIEWER_UIDS shows after a reload; the server checks every route
// on its own whatever this says.
const answered = new Map(); // uid → 'admin' | 'viewer' | null

/** Tests: forget every remembered answer. */
export function clearAdminRoleMemory() { answered.clear(); }

const isRole = (r) => r === 'admin' || r === 'viewer';

/**
 * Asks the server once. Returns { role, sure }: `sure` is false when the
 * server gave no answer about the person — a server without /me (a rolled-back
 * server answers 404), the proxy while the container restarts (502), a crash
 * (5xx), or no network.
 */
async function askServer(currentUser) {
  try {
    const token = await currentUser.getIdToken();
    const res = await fetch(`${API_BASE}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401 || res.status === 403) return { role: null, sure: true };
    if (res.ok) {
      const body = await res.json().catch(() => null);
      if (isRole(body?.role)) return { role: body.role, sure: true };
    }
  } catch { /* offline, or the token could not be had */ }
  return { role: null, sure: false };
}

/**
 * The admin panel's role for the signed-in person, as the SERVER says it
 * (GET /api/admin/me): 'admin', 'viewer' (a helper, read-only), or null.
 * docs/TODO.md Part 0, A1.
 *
 * Only a 401/403, or an answer that names a role, is the server's answer.
 * When there is none (see askServer), the founder's account still gets the
 * panel, as it did before roles, so a rollback or a restart does not lock
 * the founder out of the page that shows whether the server is dying; that
 * state comes back with `unsure: true` so the page can say so. Anyone else
 * gets nothing. This only decides what the page shows: the server checks
 * every route again.
 */
export function useAdminRole(currentUser) {
  const uid = currentUser?.uid || null;
  const known = uid && answered.has(uid);
  const [state, setState] = useState(
    known ? { loading: false, role: answered.get(uid), unsure: false } : { loading: Boolean(uid), role: null, unsure: false },
  );
  useEffect(() => {
    let live = true;
    if (!currentUser) { setState({ loading: false, role: null, unsure: false }); return undefined; }
    if (answered.has(uid)) { setState({ loading: false, role: answered.get(uid), unsure: false }); return undefined; }
    setState({ loading: true, role: null, unsure: false });
    (async () => {
      const { role, sure } = await askServer(currentUser);
      if (sure) answered.set(uid, role);
      const shown = sure ? role : (isAdminUser(currentUser) ? 'admin' : null);
      if (live) setState({ loading: false, role: shown, unsure: !sure && shown !== null });
    })();
    return () => { live = false; };
    // The person, not the object: Firebase hands out a new user object often.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);
  return state;
}
